// ========== rendering ==========
// Draws a Scene: world background + grid, pellets, trails, golden-food halos, power-ups, cells,
// viruses, shield/magnet/golden rings, floating text, then names in screen space, the HUD and screen
// effects (event wash, confetti, toasts, event pill, power-up chips, combo). It only reads the Scene,
// so a solo World and an online snapshot stream render identically.
// With a world layer attached (the three.js scene, view.ts) this canvas becomes the transparent overlay:
// the layer draws the world, and names and floaters are placed through its projection.
import { EVENTS, POWERUPS, SKIN_BY_ID, SKINS, WORLD, rad } from '../../shared/config';
import { CF_BOOST, CF_GHOST, CF_GOLDEN, CF_MAGNET, CF_SHIELD, CF_SPEED, type Scene, type SceneCell } from '../../shared/types';
import { Fx, inView, type Camera, type ViewRect } from './fx';
import { Hud, type HudFrame } from './hud';
import type { Projected, RenderMode, WorldFrame, WorldLayer } from './view';
import {
  CELL_PAD, FONT, PEL_C, PEL_R, PU_SCALE, VIRUS_PAD,
  bucketIndex, cellSprite, discSprite, pelletSprite, powerupSprite, ringSprite, vignetteSprite, virusSprite,
} from './sprites';

export type { Camera } from './fx';
export type { RenderMode, WorldLayer } from './view';
export interface RenderOpts { running: boolean; touch: boolean }

export const DPR_CAP = 2;
export const dpr = (): number => Math.min(DPR_CAP, (typeof devicePixelRatio === 'number' && devicePixelRatio) || 1);
// the 3D world's palette (render3d/palette.ts): haze outside the arena, marble floor, tile seams every 250 units
const BG = '#d98a66', WORLD_BG = '#ecdcc8', GRID_COLOR = '#d6bea4', GRID = 250;
const TOUCH_LIFT = 116; // keep bottom-centre HUD clear of the touch buttons
const HUD_TOP = 16; // toasts' top edge when the HUD doesn't push them down
const BOOST_TRAIL_EVERY = 0.035, SPEED_TRAIL_EVERY = 0.03; // afterimages on a timer, same at 60 and 120 Hz
const SPEED_TRAIL_COLOR = '#facc15';
const RING_FLAGS = CF_SHIELD | CF_MAGNET | CF_GOLDEN, TRAIL_FLAGS = CF_BOOST | CF_SPEED;
const SHIELD_DASH = [14, 8];

// zoom by screen area, so a phone in portrait sees as much of the map as a laptop
export function targetZoom(mass: number, W: number, H: number): number {
  return Math.pow(Math.min(1, 40 / rad(Math.max(0, mass))), 0.45) * Math.max(Math.sqrt(W * H / 1.6e6), 0.45);
}
// mass-weighted centre of some cells into out; false if they have no mass
export function massCentre(cells: readonly SceneCell[], out: { x: number; y: number }): boolean {
  let x = 0, y = 0, m = 0;
  for (const c of cells) { x += c.x * c.mass; y += c.y * c.mass; m += c.mass; }
  if (m <= 0) return false;
  out.x = x / m; out.y = y / m;
  return true;
}

// per-cell render state, keyed by cell id: displayed radius and trail timers
interface CellView { dr: number; seen: number; boostAt: number; speedAt: number }
const byMass = (a: SceneCell, b: SceneCell) => a.mass - b.mass;
const labelFonts: string[] = [], massFonts: string[] = [];

export class Renderer {
  readonly fx = new Fx();
  readonly hud = new Hud();
  camera: Camera = { x: WORLD / 2, y: WORLD / 2, z: 1 };
  private ctx: CanvasRenderingContext2D;
  private views = new Map<number, CellView>();
  private frame = 0;
  private following = false; // camera is tracking my cells (snaps on spawn)
  private drawList: SceneCell[] = []; // visible cells, reused every frame
  private view: ViewRect = { x0: 0, y0: 0, x1: 0, y1: 0 };
  private centre = { x: 0, y: 0 };
  private tint = { color: '', a: 0 };
  private hf: HudFrame;
  private W = 0; private H = 0;
  // world layer (overlay mode) and its per-frame hand-off, reused
  private world: WorldLayer | null = null;
  private wf: WorldFrame;
  private proj: Projected = { x: 0, y: 0, depth: 0 };
  private loose: ViewRect = { x0: 0, y0: 0, x1: 0, y1: 0 };
  // see view.ts; 'full' until main.ts says otherwise, so the renderer works on its own
  mode: RenderMode = 'full';
  // the world layer threw while drawing: it has been detached and disposed, and 2D draws from that frame on
  onWorldLost: ((err: unknown) => void) | null = null;
  // displayed radius eases toward the true radius so growth/shrink never pops
  readonly dispR = (c: SceneCell): number => { const v = this.views.get(c.id); return v ? v.dr : c.r; };
  // world -> screen through the world layer (floaters, confetti), on the floor
  private readonly toScreen = (x: number, y: number, out: Projected): Projected =>
    this.world ? this.world.project(x, y, 0, out) : this.worldToScreen(x, y, out) as Projected;

  constructor(private readonly canvas: HTMLCanvasElement) {
    // not opaque: over a world layer this canvas is cleared to transparent every frame
    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) throw new Error('canvas 2d context unavailable');
    this.ctx = ctx;
    this.hf = { W: 0, H: 0, d: 1, t: 0, cam: this.camera, cx: 0, cy: 0, lift: 0, dispR: this.dispR };
    this.wf = { scene: null as unknown as Scene, cam: this.camera, W: 0, H: 0, dpr: 1, dt: 0, t: 0, dispR: this.dispR,
      trails: this.fx.trails, nTrails: 0, running: false };
  }

  // 'pending' at boot until a world layer attaches; 'full' when none will (no WebGPU/WebGL2)
  setMode(mode: 'pending' | 'full'): void {
    this.detachWorld()?.dispose();
    this.mode = mode;
  }
  attachWorld(world: WorldLayer): void {
    this.world = world; this.mode = 'overlay';
    this.fx.toScreen = this.toScreen;
  }
  // back to the full 2D renderer; hands the layer back to whoever disposes of it
  detachWorld(): WorldLayer | null {
    const w = this.world;
    this.world = null; this.mode = 'full';
    this.fx.toScreen = null;
    return w;
  }

  // a screen offset from the centre (mouse or joystick, CSS px) -> world offset from the camera point:
  // through the world layer's floor when it draws the world (its camera is tilted), else the zoom
  aimOffset(ax: number, ay: number, out: { x: number; y: number } = { x: 0, y: 0 }): { x: number; y: number } {
    const cam = this.camera, w = this.world;
    if (w && w.ground(this.W / 2 + ax, this.H / 2 + ay, out)) { out.x -= cam.x; out.y -= cam.y; return out; }
    out.x = ax / cam.z; out.y = ay / cam.z;
    return out;
  }

  // viewport size in CSS px (as of the last render)
  get width(): number { return this.W; }
  get height(): number { return this.H; }
  // screen CSS px -> world units, with the current camera (for aiming)
  screenToWorld(sx: number, sy: number, out: { x: number; y: number } = { x: 0, y: 0 }): { x: number; y: number } {
    const c = this.camera;
    out.x = c.x + (sx - this.W / 2) / c.z; out.y = c.y + (sy - this.H / 2) / c.z;
    return out;
  }
  worldToScreen(x: number, y: number, out: { x: number; y: number } = { x: 0, y: 0 }): { x: number; y: number } {
    const c = this.camera;
    out.x = (x - c.x) * c.z + this.W / 2; out.y = (y - c.y) * c.z + this.H / 2;
    return out;
  }

  private resize(W: number, H: number, d: number): void {
    const cw = Math.round(W * d), ch = Math.round(H * d);
    if (this.canvas.width === cw && this.canvas.height === ch) return; // also catches a move to another display
    this.canvas.width = cw; this.canvas.height = ch;
    this.canvas.style.width = W + 'px'; this.canvas.style.height = H + 'px';
  }

  render(scene: Scene, dt: number, opts: { running: boolean; touch: boolean }): void {
    const d = dpr(), W = innerWidth, H = innerHeight, ctx = this.ctx, cam = this.camera, fx = this.fx;
    this.W = W; this.H = H;
    this.resize(W, H, d);
    ctx.setTransform(d, 0, 0, d, 0, 0);
    ctx.globalAlpha = 1;
    // the world: a world layer's, the 2D one (also a game started before the layer arrived), or none yet
    let world = this.mode === 'overlay' ? this.world : null;
    let flat = !world && (this.mode === 'full' || opts.running);
    ctx.clearRect(0, 0, W, H);
    fx.w = W; fx.h = H;
    fx.update(dt);

    const ease = (k: number) => 1 - Math.exp(-k * dt); // frame-rate independent smoothing
    this.easeRadii(scene, ease(14));
    const centre = this.centre;
    if (scene.alive && massCentre(scene.myCells, centre)) {
      let m = scene.myMass;
      if (!(m > 0)) { m = 0; for (const c of scene.myCells) m += c.mass; }
      const z = targetZoom(m, W, H);
      if (!this.following) { cam.x = centre.x; cam.y = centre.y; this.following = true; } // fresh spawn
      else { cam.x += (centre.x - cam.x) * ease(10); cam.y += (centre.y - cam.y) * ease(10); }
      cam.z += (z - cam.z) * ease(3);
    } else {
      this.following = false;
      centre.x = cam.x; centre.y = cam.y;
    }

    const z = cam.z, view = this.view;
    view.x0 = cam.x - W / 2 / z; view.x1 = cam.x + W / 2 / z; view.y0 = cam.y - H / 2 / z; view.y1 = cam.y + H / 2 / z;
    this.spawnTrails(scene);

    if (world) {
      const f = this.wf;
      f.scene = scene; f.W = W; f.H = H; f.dpr = d; f.dt = dt; f.t = fx.t; f.nTrails = fx.nTrails; f.running = opts.running;
      try {
        world.draw(f);
      } catch (err) {
        // a throw from the GPU side would repeat every frame and take the names, HUD and death screen with it
        this.detachWorld();
        try { world.dispose(); } catch { /* already broken */ }
        this.onWorldLost?.(err);
        world = null; flat = true;
      }
    }
    if (flat) {
      ctx.fillStyle = BG; ctx.fillRect(0, 0, W, H);
      this.drawFlat(scene, z * d);
      this.drawLabels(W, H, null);
    } else if (world) {
      this.labelList(scene);
      this.drawLabels(W, H, world);
      fx.drawFloaters(ctx, 1, this.toScreen);
    }
    const hf = this.hf;
    hf.W = W; hf.H = H; hf.d = d; hf.t = fx.t; hf.cam = cam; hf.cx = centre.x; hf.cy = centre.y; hf.lift = opts.touch ? TOUCH_LIFT : 0;
    if (opts.running) this.hud.draw(ctx, scene, hf);
    this.drawScreenFx(scene, opts.running, dt, d);
  }

  // the whole world in 2D, inside the camera transform; leaves drawList = visible cells, smallest first
  private drawFlat(scene: Scene, devScale: number): void {
    const ctx = this.ctx, cam = this.camera, fx = this.fx, z = cam.z, t = scene.now, view = this.view;
    ctx.save();
    ctx.translate(this.W / 2, this.H / 2); ctx.scale(z, z); ctx.translate(-cam.x, -cam.y);
    this.drawGround(view, z);

    // pellets
    let rush = false;
    const pel = scene.pellets;
    for (let i = 0; i < pel.length; i++) {
      const f = pel[i];
      if (!inView(view, f.x, f.y, f.r * 2)) continue;
      if (f.kind === 2) rush = true;
      const h = f.r * PEL_C / PEL_R;
      ctx.drawImage(pelletSprite(f.color), f.x - h, f.y - h, h * 2, h * 2);
    }
    const list = this.drawList;
    list.length = 0;
    for (let i = 0; i < scene.cells.length; i++) {
      const c = scene.cells[i];
      if (inView(view, c.x, c.y, this.dispR(c) * CELL_PAD)) list.push(c);
    }
    list.sort(byMass);

    // world fx: trails, shimmering golden food, power-ups
    fx.drawTrails(ctx, view);
    if (rush) {
      const halo = discSprite('#fde68a');
      for (let i = 0; i < pel.length; i++) {
        const f = pel[i];
        if (f.kind !== 2 || !inView(view, f.x, f.y, f.r * 1.8)) continue;
        const hr = f.r * 1.8;
        ctx.globalAlpha = 0.25 + 0.25 * Math.sin(t * 6 + f.ph);
        ctx.drawImage(halo, f.x - hr, f.y - hr, hr * 2, hr * 2);
      }
      ctx.globalAlpha = 1;
    }
    for (const pu of scene.powerups) {
      const bob = Math.sin(t * 2.5 + pu.ph) * 6;
      if (!inView(view, pu.x, pu.y + bob, 60)) continue;
      const s = (1 + 0.12 * Math.sin(t * 4 + pu.ph)) * Math.min(1, (t - pu.born) * 3) / PU_SCALE;
      if (s <= 0) continue;
      const disc = powerupSprite(pu.type), ring = ringSprite(POWERUPS[pu.type].color);
      ctx.save();
      ctx.translate(pu.x, pu.y + bob); ctx.scale(s, s);
      ctx.drawImage(disc, -disc.width / 2, -disc.height / 2);
      ctx.rotate(t * 1.5 + pu.ph);
      ctx.drawImage(ring, -ring.width / 2, -ring.height / 2);
      ctx.restore();
    }

    // cells, smallest first, then viruses on top
    const ghostA = 0.5 + 0.1 * Math.sin(t * 6);
    for (let i = 0; i < list.length; i++) {
      const c = list[i], r = this.dispR(c), h = r * CELL_PAD;
      ctx.globalAlpha = c.flags & CF_GHOST ? ghostA : 1;
      ctx.drawImage(cellSprite(c.skin, bucketIndex(r * devScale)), c.x - h, c.y - h, h * 2, h * 2);
    }
    ctx.globalAlpha = 1;
    for (const v of scene.viruses) {
      const r = v.r, h = r * VIRUS_PAD;
      if (!inView(view, v.x, v.y, h)) continue;
      ctx.drawImage(virusSprite(bucketIndex(r * devScale)), v.x - h, v.y - h, h * 2, h * 2);
    }
    this.drawRings(scene, t);
    fx.drawFloaters(ctx, z);
    ctx.restore();
  }

  // cells to name over a world layer: its tilted camera shows up to 1.2x the 2D view (render3d/camera.ts)
  private labelList(scene: Scene): void {
    const v = this.view, l = this.loose, list = this.drawList, mx = (v.x1 - v.x0) * 0.15, my = (v.y1 - v.y0) * 0.15;
    l.x0 = v.x0 - mx; l.x1 = v.x1 + mx; l.y0 = v.y0 - my; l.y1 = v.y1 + my;
    list.length = 0;
    for (let i = 0; i < scene.cells.length; i++) {
      const c = scene.cells[i];
      if (inView(l, c.x, c.y, this.dispR(c) * CELL_PAD)) list.push(c);
    }
    list.sort(byMass);
  }

  // eases every cell's displayed radius; forgets cells that left the scene
  private easeRadii(scene: Scene, k: number): void {
    const f = ++this.frame, views = this.views;
    for (const c of scene.cells) {
      const v = views.get(c.id);
      if (!v) { views.set(c.id, { dr: c.r, seen: f, boostAt: 0, speedAt: 0 }); continue; }
      v.dr = v.seen === f - 1 ? v.dr + (c.r - v.dr) * k : c.r; // a returning id starts fresh
      v.seen = f;
    }
    if (f % 60 === 0) for (const [id, v] of views) if (v.seen < f - 1) views.delete(id);
  }

  // boost afterimages in the skin colour and speed sparks, for every cell near the screen
  private spawnTrails(scene: Scene): void {
    const fx = this.fx, t = fx.t, view = this.view;
    for (const c of scene.cells) {
      if (!(c.flags & TRAIL_FLAGS) || !inView(view, c.x, c.y, c.r + 200)) continue;
      const v = this.views.get(c.id);
      if (!v) continue;
      if (c.flags & CF_BOOST && t >= v.boostAt) {
        v.boostAt = t + BOOST_TRAIL_EVERY;
        fx.trail(c.x, c.y, c.r * 0.5, (SKIN_BY_ID[c.skin] || SKINS[0]).color);
      }
      if (c.flags & CF_SPEED && t >= v.speedAt) {
        v.speedAt = t + SPEED_TRAIL_EVERY;
        const r = c.r;
        fx.trail(c.x + (Math.random() * 2 - 1) * r * 0.5, c.y + (Math.random() * 2 - 1) * r * 0.5, r * 0.35, SPEED_TRAIL_COLOR);
      }
    }
  }

  private drawGround(view: ViewRect, z: number): void {
    const ctx = this.ctx;
    ctx.fillStyle = WORLD_BG; ctx.fillRect(0, 0, WORLD, WORLD);
    ctx.strokeStyle = GRID_COLOR; ctx.lineWidth = 1 / z;
    ctx.beginPath();
    const ya = Math.max(0, view.y0), yb = Math.min(WORLD, view.y1), xa = Math.max(0, view.x0), xb = Math.min(WORLD, view.x1);
    for (let x = Math.max(0, Math.floor(view.x0 / GRID) * GRID); x <= xb; x += GRID) { ctx.moveTo(x, ya); ctx.lineTo(x, yb); }
    for (let y = Math.max(0, Math.floor(view.y0 / GRID) * GRID); y <= yb; y += GRID) { ctx.moveTo(xa, y); ctx.lineTo(xb, y); }
    ctx.stroke();
  }

  // shield (rotating rainbow dashes), magnet (expanding pulse), golden blob (pulsing gold)
  private drawRings(scene: Scene, t: number): void {
    const ctx = this.ctx, view = this.view;
    for (const c of scene.cells) {
      if (!(c.flags & RING_FLAGS)) continue;
      const r = this.dispR(c);
      if (c.flags & CF_SHIELD && inView(view, c.x, c.y, r + 15)) {
        ctx.save(); ctx.translate(c.x, c.y); ctx.rotate(t * 2);
        ctx.strokeStyle = `hsla(${(t * 120) % 360},90%,60%,0.9)`; ctx.lineWidth = 5; ctx.setLineDash(SHIELD_DASH);
        ctx.beginPath(); ctx.arc(0, 0, r + 10, 0, Math.PI * 2); ctx.stroke(); ctx.restore();
      }
      if (c.flags & CF_MAGNET && inView(view, c.x, c.y, r + 220)) {
        const k = (t * 1.2) % 1;
        ctx.strokeStyle = `rgba(244,114,182,${0.5 * (1 - k)})`; ctx.lineWidth = 3;
        ctx.beginPath(); ctx.arc(c.x, c.y, r + 20 + k * 200, 0, Math.PI * 2); ctx.stroke();
      }
      if (c.flags & CF_GOLDEN && inView(view, c.x, c.y, r + 10)) {
        ctx.strokeStyle = `rgba(250,204,21,${0.5 + 0.4 * Math.sin(t * 8)})`; ctx.lineWidth = 6;
        ctx.beginPath(); ctx.arc(c.x, c.y, r + 6, 0, Math.PI * 2); ctx.stroke();
      }
    }
  }

  // names are drawn in screen space at capped sizes, so huge cells don't need huge glyphs; over a world
  // layer they sit on the projected top of the cell, sized by the scale there
  private drawLabels(W: number, H: number, world: WorldLayer | null): void {
    const ctx = this.ctx, cam = this.camera, list = this.drawList, p = this.proj;
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    for (let i = 0; i < list.length; i++) {
      const c = list[i], r = this.dispR(c);
      if (r < 20) continue;
      let sx: number, sy: number, k: number;
      if (world) { world.project(c.x, c.y, world.cellTop(r), p); sx = p.x; sy = p.y; k = world.scaleAt(c.x, c.y); }
      else { sx = (c.x - cam.x) * cam.z + W / 2; sy = (c.y - cam.y) * cam.z + H / 2; k = cam.z; }
      const fs = Math.round(Math.min(64, Math.max(11, r * 0.3) * k));
      if (fs < 7) continue;
      ctx.font = labelFonts[fs] || (labelFonts[fs] = `600 ${fs}px ${FONT}`);
      ctx.fillStyle = 'rgba(0,0,0,0.25)'; ctx.fillText(c.name, sx, sy + fs * 0.06);
      ctx.fillStyle = '#fff'; ctx.fillText(c.name, sx, sy);
      if (r > 40 && fs >= 12) {
        ctx.font = massFonts[fs] || (massFonts[fs] = `500 ${Math.round(fs * 0.5)}px ${FONT}`);
        ctx.fillStyle = 'rgba(255,255,255,0.8)';
        ctx.fillText(String(Math.floor(c.mass)), sx, sy + fs * 0.8);
      }
    }
  }

  private drawScreenFx(scene: Scene, running: boolean, dt: number, d: number): void {
    const ctx = this.ctx, fx = this.fx, W = this.W, H = this.H, tint = this.tint, hf = this.hf;
    // colour wash that fades in during events
    if (scene.event) tint.color = EVENTS[scene.event.type].color;
    const target = scene.event ? 0.07 + 0.03 * Math.sin(fx.t * 2) : 0;
    tint.a += (target - tint.a) * (1 - Math.exp(-3 * dt));
    if (tint.color && tint.a > 0.003) {
      ctx.globalAlpha = Math.min(1, tint.a / 0.1);
      ctx.drawImage(vignetteSprite(tint.color), 0, 0, W, H);
      ctx.globalAlpha = 1;
    }
    fx.drawConfetti(ctx, d);
    if (!running) return;
    fx.drawToasts(ctx, W, this.hud.bottom || HUD_TOP, d);
    this.hud.drawEventPill(ctx, scene, hf);
    this.hud.drawPowerChips(ctx, scene, hf);
    fx.drawCombo(ctx, W, H, hf.lift);
  }
}
